/**
 * The snippet the artist pastes, built once and used twice: the code block
 * shows it and the copy button copies it. Both read this, so what is on screen
 * is what lands on the clipboard.
 */

/** The frame's height. Unchanged from the snippet the app handed out before. */
export const EMBED_HEIGHT = 500;

/** The width the app used before the artist could choose one. */
export const EMBED_DEFAULT_WIDTH = 400;
export const EMBED_MIN_WIDTH = 240;
export const EMBED_MAX_WIDTH = 720;
export const EMBED_WIDTH_STEP = 10;

export interface EmbedSnippetAttribute {
  name: string;
  value: string;
}

export interface EmbedSnippetOptions {
  embedUrl: string;
  /** The width the artist chose, in CSS pixels. */
  width?: number;
  /** The frame's accessible name — "<page> — <artist>". Dropped when unknown. */
  title?: string | null;
}

/** An attribute value goes inside double quotes, so those four have to go. */
function escapeAttributeValue(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * The attributes, in the order they are read.
 *
 * `max-width:100%` is what makes the slider honest: the width is a cap, and a
 * column narrower than it shrinks the frame instead of overflowing.
 * `allow="encrypted-media"` stays — the page carries players that need it.
 */
export function embedSnippetAttributes({ embedUrl, width, title }: EmbedSnippetOptions): EmbedSnippetAttribute[] {
  const attributes: EmbedSnippetAttribute[] = [
    { name: 'src', value: escapeAttributeValue(embedUrl) },
    { name: 'width', value: `${width ?? EMBED_DEFAULT_WIDTH}` },
    { name: 'height', value: `${EMBED_HEIGHT}` },
    { name: 'style', value: 'border:0;max-width:100%' },
    { name: 'loading', value: 'lazy' },
    { name: 'allow', value: 'encrypted-media' },
  ];
  if (title !== undefined && title !== null && title !== '') {
    attributes.push({ name: 'title', value: escapeAttributeValue(title) });
  }
  return attributes;
}

export function embedSnippetCode(attributes: EmbedSnippetAttribute[]): string {
  const pairs = attributes.map((attribute) => `${attribute.name}="${attribute.value}"`).join(' ');
  return `<iframe ${pairs}></iframe>`;
}
